import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {Typography} from '@mui/material';
import {t} from 'i18next';
import _ from 'lodash';
import ProcessingDataSetInfoCard, {
  ProcessingDataSetType,
} from './ProcessingDataSetInfoCard';
import {RootState} from '../../../../redux/store';
import {getProcessingInProgressReport} from '../../../../api/ReportingManagement';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridNoRowsOverlay from '../../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {ReportScreenTabTypes} from '../../../pages/reports/ProjectOverview';
import {failedToLoadStatusCodes} from '../../../../constants/constants';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REPORT_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
interface Props {
  openSpecificTab?: (type: ReportScreenTabTypes) => void;
}
const ProcessingInProgressList: React.FC<Props> = ({openSpecificTab}) => {
  const {trackGAEvent} = useAnalytics();

  const {projectId, workspaceId, mediaEvidenceId, clientId} = useSelector(
    (state: RootState) => state.reports
  );

  const [processingDataSets, setProcessingDataSets] = useState<
    ProcessingDataSetType[]
  >([]);
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [isRequestFailed, setIsRequestFailed] = useState<boolean>(false);
  const apiResponseTime = useRef('');
  const [isEventTracked, setIsEventTracked] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [mediaEvidenceId]);

  const fetchReports = async () => {
    if (projectId && workspaceId && clientId) {
      const queryParam = {
        clientId: clientId,
        projectId: projectId,
        workspaceId: workspaceId,
        evidenceSource: mediaEvidenceId ?? '', // To get all in progress data
      };
      setReqLoading(true);
      try {
        setIsRequestFailed(false);
        const response = await getProcessingInProgressReport(
          new URLSearchParams(queryParam).toString()
        );
        const data = _.get(
          response,
          'data.data',
          []
        ) as ProcessingDataSetType[];
        apiResponseTime.current = response.data.responseTime;
        setProcessingDataSets(data);
      } catch (error: unknown) {
        setProcessingDataSets([]);
        if (
          failedToLoadStatusCodes.includes((error as {status: number}).status)
        )
          setIsRequestFailed(true);
        apiResponseTime.current = (error as any).responseTime;
      } finally {
        setReqLoading(false);

        !isEventTracked &&
          trackGAEvent({
            eventName: GA_EVENT_NAMES.PAGE_VIEW,
            page: GA_DATA_MODE_AUTO,
            user: GA_DATA_MODE_AUTO,
            report: {
              name: t('gaTracking.reports.tabProcessingInProgress'),
              type: GA_REPORT_TYPE.STANDARD,
              load_time: apiResponseTime.current,
            },
          });
        setIsEventTracked(true);
      }
    }
  };

  const handleRefreshDataSet = (data: ProcessingDataSetType) => {
    setProcessingDataSets((state) => {
      const updateList = [...state];
      const dataSetIndex = updateList.findIndex(
        (item) => item.dataset === data.dataset
      );
      if (dataSetIndex !== -1) updateList[dataSetIndex] = data;
      return updateList;
    });
  };

  // const renderAllProcessFinishCard = () => {
  //   return (
  //     <div className="all-processing-completed-card">
  //       <Typography className="typo-text subhead2_size all-finish-text">
  //         {t('reports.processingInProgress.allProcessCompleted')}
  //       </Typography>
  //       <AurButton
  //         type="text"
  //         size="Medium"
  //         classNames="view-summary-button-container"
  //         label={t(
  //           'reports.processingInProgress.datasetCard.viewProcessingSummary'
  //         )}
  //         endIcon={<RightArrowIcon width={24} height={24} />}
  //         onClick={handleViewAllSummaryClick}
  //       />
  //     </div>
  //   );
  // };

  const renderProcessingCards = () => {
    if (reqLoading) {
      return (
        <div className="data-loading">
          <AurLoadingIndicator />
        </div>
      );
    }
    if (isRequestFailed) {
      return (
        <div className="processing-in-progress-data-error">
          <AurDataGridErrorOverlay onRefresh={fetchReports} />
        </div>
      );
    }

    if (processingDataSets.length === 0) {
      return <AurDataGridNoRowsOverlay />;
    }

    return (
      <>
        {processingDataSets.map((item, index) => (
          <ProcessingDataSetInfoCard
            data={item}
            key={'dataset_' + index}
            openSpecificTab={openSpecificTab}
            onRefreshData={handleRefreshDataSet}
          />
        ))}
      </>
    );
  };

  return (
    <div className="processing-in-progress-container">
      <div className="header-content-container">
        <Typography
          aria-label={t('reports.processingInProgress.title')}
          className="typo-text subhead1_size common-mist-100-text"
          component="h1"
        >
          {t('reports.processingInProgress.title')}
        </Typography>
        <Typography
          aria-label={t('reports.processingInProgress.desc')}
          className="typo-text body2_size desc-text"
        >
          {t('reports.processingInProgress.desc')}
        </Typography>
      </div>

      {renderProcessingCards()}
      {/* {renderAllProcessFinishCard()} */}
    </div>
  );
};

export default ProcessingInProgressList;
